import * as THREE from 'three/webgpu';
import {
  uniform, uv, vec2, vec3, float, mix, smoothstep, length, dot, pow, max,
  attribute, positionLocal, normalize, sin, clamp,
} from 'three/tsl';

import { uTime, uLightning } from '../core/uniforms.js';
import { streamRng, range } from '../math/rng.js';
import { RENDER_ORDER } from './materials.js';

/**
 * @file The sky dome: a 3-stop vertical gradient, a seeded star field, and sun +
 * moon billboards. Replaces `engine.scene.background`.
 *
 * ⚠ THE SUN DISC IS COSMETIC. The bake's `sunDir` is a LEVEL CONSTANT (see
 * `types.js` -> `LevelMeta.sunDir` and `BakeConfig.sunDir`): shading, AO and the
 * projected shadow decals are all fixed at build time. The disc you see moving
 * across this dome is set-dressing that happens to share a name. Nobody may
 * re-bake, re-project or re-orient anything from `DayNight.sunDir` — that is the
 * whole trade that buys the no-shadow-map look.
 *
 * Geometry notes:
 *  - The dome is `BackSide`, `depthWrite:false`, `renderOrder -1000`, and
 *    OPAQUE, so it lands first in the opaque queue and acts as a clear colour.
 *  - Stars / sun / moon are additive + `depthTest:true`, so buildings correctly
 *    occlude them.
 *  - Radius must stay inside the camera's far plane. {@link attachSky} pins the
 *    dome to the camera every frame, which makes that automatic; a dome parented
 *    to a level root instead only works while the camera stays near the origin.
 *
 * @module engine/render/sky
 */

/** @typedef {import('../core/Engine.js').Engine} Engine */

/**
 * @typedef {Object} SkyTime
 * @property {number} [night=0] 0..1.
 * @property {number} [warm=0] 0..1, the golden band. Used as `sunset` when that
 *   is not supplied.
 * @property {number} [sunset] 0..1, explicit golden-band amount.
 * @property {number} [day=1] 0..1.
 * @property {{x:number,y:number,z:number}} [sunDir] Unit vector, surface -> sun.
 *   VISUAL ONLY (see the file header).
 */

/**
 * @typedef {Object} SkyHandle
 * @property {THREE.Group} object Add to a scene or a level root.
 * @property {(t: SkyTime) => void} setTime
 * @property {(w: {overcast?: number}) => void} setWeather Cloud cover, 0..1.
 *   STICKY and independent of {@link SkyTime}: it is stored and re-applied on
 *   every later `setTime`, so `DayNight` and `Weather` can drive the same dome
 *   without either knowing the other exists.
 * @property {() => void} dispose Idempotent.
 * @property {Object} palette The live colour stops, for debugging/tweaking.
 */

/**
 * Cloud grade for ONE sky stop, derived from the stop itself rather than from a
 * second palette.
 *
 * That is the whole trick: a hand-authored "overcast palette" would need a day
 * variant AND a dusk variant AND a night variant to avoid a grey noon sky at
 * 3am, i.e. it would triple the thing the three-stop palette exists to avoid.
 * Desaturating and darkening whatever the cycle already produced works at every
 * hour by construction, and keeps `STOPS` the single source of the art bar.
 *
 * @param {THREE.Color} c MUTATED in place.
 * @param {number} amt 0..1.
 * @param {THREE.Color} tmp Scratch.
 */
function cloudStop(c, amt, tmp) {
  if (amt <= 0) return;
  // Rec.709 luma, then pulled a touch blue: cloud is grey, but a cloud lit from
  // above is a COOL grey, and a neutral one reads as a rendering failure.
  const l = c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722;
  tmp.setRGB(l * 0.88, l * 0.91, l * 0.98);
  c.lerp(tmp, amt * 0.82);
  // ...and then down: an overcast sky is dimmer than the sun it is hiding.
  c.multiplyScalar(1 - amt * 0.2);
}

// ── palette ──────────────────────────────────────────────────────────────────
// The project's reference look: dusty amber day, salmon dusk, teal-indigo night.
// Changing these changes the whole game's mood — they are the art bar.

/**
 * Below-horizon haze, horizon band, mid band, zenith.
 * Exported as {@link SKY_STOPS} so `render/fog.js` callers can match the fog
 * colour to the horizon stop — that match is what makes the seam where the
 * ground meets the sky disappear.
 */
const STOPS = {
  day: { ground: '#b09b78', horizon: '#ecdfc4', mid: '#86b6dd', zenith: '#3a72b6' },
  dusk: { ground: '#79453e', horizon: '#ffb277', mid: '#e2705c', zenith: '#4a3d73' },
  night: { ground: '#0a1120', horizon: '#26374f', mid: '#122140', zenith: '#040814' },
};

/** @see STOPS */
export const SKY_STOPS = STOPS;

const SUN_DAY = '#fff3d4';
const SUN_DUSK = '#ff9a55';
const MOON_COLOR = '#cfdcf7';

/** day -> dusk -> night, as two successive lerps. Matches the sibling project. */
const mix3 = (() => {
  const tmp = new THREE.Color();
  /**
   * @param {THREE.Color} out
   * @param {string|number} day
   * @param {string|number} dusk
   * @param {string|number} night
   * @param {number} duskAmt
   * @param {number} nightAmt
   */
  return (out, day, dusk, night, duskAmt, nightAmt) =>
    out.set(day).lerp(tmp.set(dusk), duskAmt).lerp(tmp.set(night), nightAmt);
})();

/**
 * Builds the sky.
 *
 * @param {Object} [o]
 * @param {number} [o.radius=450] Dome radius in metres.
 * @param {number} [o.starCount=460]
 * @param {string|number} [o.seed='sky'] Star placement seed (deterministic).
 * @param {number} [o.sunSize=0.055] Sun disc diameter as a fraction of `radius`.
 * @param {number} [o.moonSize=0.042]
 * @param {boolean} [o.stars=true]
 * @returns {SkyHandle}
 */
export function createSky(o = {}) {
  const {
    radius = 450,
    starCount = 460,
    seed = 'sky',
    sunSize = 0.055,
    moonSize = 0.042,
    stars = true,
  } = o;

  const group = new THREE.Group();
  group.name = 'sky';
  // The dome is always around the camera; culling it is never right and the
  // bounding sphere of a 450m ball is a waste of time to test.
  group.frustumCulled = false;

  // ── uniforms (everything time-of-day is a uniform; setTime never rebuilds) ──
  const uGround = uniform(new THREE.Color(STOPS.day.ground));
  const uHorizon = uniform(new THREE.Color(STOPS.day.horizon));
  const uMid = uniform(new THREE.Color(STOPS.day.mid));
  const uZenith = uniform(new THREE.Color(STOPS.day.zenith));
  const uSunDir = uniform(new THREE.Vector3(0.42, 0.67, 0.61).normalize());
  const uGlowColor = uniform(new THREE.Color(SUN_DAY));
  const uGlowAmt = uniform(0.25);
  const uStarOpacity = uniform(0);
  const uSunOpacity = uniform(1);
  const uSunColor = uniform(new THREE.Color(SUN_DAY).multiplyScalar(2.6));
  const uMoonOpacity = uniform(0);

  // ── dome ───────────────────────────────────────────────────────────────────
  const domeGeo = new THREE.SphereGeometry(radius, 40, 24);
  const domeMat = new THREE.MeshBasicNodeMaterial({ side: THREE.BackSide });
  domeMat.name = 'sky.dome';
  domeMat.fog = false; // fog would flatten the gradient into one colour
  domeMat.depthWrite = false;
  domeMat.toneMapped = false;

  {
    const dir = normalize(positionLocal);
    const t = dir.y;
    // 3 stops + a below-horizon haze so the dome reads as a world, not a bowl.
    //
    // The stops are packed HARD against the horizon on purpose: a top-down game
    // camera only ever sees the first ~10° of sky (see the note in
    // `dev/scenes/daynight.js`), so a physically-spaced gradient would render as
    // one flat pale band. Everything interesting has to happen by t≈0.15.
    const lower = mix(uGround, uHorizon, smoothstep(float(-0.28), float(-0.004), t));
    const band = mix(lower, uMid, smoothstep(float(0.0), float(0.15), t));
    const grad = mix(band, uZenith, smoothstep(float(0.13), float(0.62), t));

    // Warm halo around the sun, in TWO lobes. A single wide lobe reads as a
    // mushroom cloud once bloom gets hold of it; a wide-but-faint wash plus a
    // tight-but-bright core is what actually looks like a low sun.
    const cosA = max(dot(dir, uSunDir), float(0));
    const wash = pow(cosA, float(2.5)).mul(uGlowAmt).mul(0.32);
    const core = pow(cosA, float(48.0)).mul(uGlowAmt).mul(0.85);

    // LIGHTNING. A uniform, so the flash costs nothing when there is no storm
    // and needs no CPU write per frame. `mul` first so a night sky (near black)
    // still lifts off its own floor, then a flat add so the dome cannot stay
    // black where the gradient is; both are ZERO at `uLightning = 0`.
    const flashed = grad
      .mul(float(1).add(uLightning.mul(2.1)))
      .add(vec3(0.30, 0.36, 0.46).mul(uLightning));

    domeMat.colorNode = flashed.add(uGlowColor.mul(wash.add(core)));
  }

  const dome = new THREE.Mesh(domeGeo, domeMat);
  dome.name = 'sky.dome';
  dome.renderOrder = RENDER_ORDER.sky;
  dome.frustumCulled = false;
  group.add(dome);

  // ── stars ──────────────────────────────────────────────────────────────────
  /** @type {THREE.BufferGeometry|null} */
  let starGeo = null;
  /** @type {THREE.Material|null} */
  let starMat = null;

  if (stars && starCount > 0) {
    starGeo = buildStarQuads(starCount, radius * 0.965, seed);
    starMat = new THREE.MeshBasicNodeMaterial();
    starMat.name = 'sky.stars';
    starMat.transparent = true;
    starMat.depthWrite = false;
    starMat.blending = THREE.AdditiveBlending;
    starMat.fog = false;
    starMat.toneMapped = false;

    const tint = attribute('starTint', 'vec3');
    const phase = attribute('starPhase', 'float');
    const d = length(uv().sub(vec2(0.5, 0.5))).mul(2);
    const mask = smoothstep(float(1.0), float(0.1), d);
    // Slow, shallow twinkle. Fast twinkle looks like a bug, not a star.
    const twinkle = sin(uTime.mul(1.7).add(phase)).mul(0.16).add(0.84);

    starMat.colorNode = tint;
    starMat.opacityNode = mask.mul(twinkle).mul(uStarOpacity);

    const starMesh = new THREE.Mesh(starGeo, starMat);
    starMesh.name = 'sky.stars';
    starMesh.renderOrder = RENDER_ORDER.sky + 1;
    starMesh.frustumCulled = false;
    group.add(starMesh);
  }

  // ── sun + moon billboards ──────────────────────────────────────────────────
  // A quad on the dome facing its centre is a perfect billboard, because the
  // dome centre IS the camera (see attachSky) — no per-frame lookAt needed.
  const discGeo = new THREE.PlaneGeometry(1, 1);

  const sunMat = discMaterial('sky.sun', uSunColor, uSunOpacity, 0.3, 2.2);
  const sun = new THREE.Mesh(discGeo, sunMat);
  sun.name = 'sky.sun';
  sun.renderOrder = RENDER_ORDER.sky + 2;
  sun.frustumCulled = false;
  sun.scale.setScalar(radius * sunSize * 3.4); // 3.4x the disc, room for the halo
  group.add(sun);

  const uMoonColor = uniform(new THREE.Color(MOON_COLOR).multiplyScalar(1.6));
  const moonMat = discMaterial('sky.moon', uMoonColor, uMoonOpacity, 0.34, 3.2);
  const moon = new THREE.Mesh(discGeo, moonMat);
  moon.name = 'sky.moon';
  moon.renderOrder = RENDER_ORDER.sky + 2;
  moon.frustumCulled = false;
  moon.scale.setScalar(radius * moonSize * 3.0);
  group.add(moon);

  // ── setTime ────────────────────────────────────────────────────────────────
  const _sunDir = new THREE.Vector3(0.42, 0.67, 0.61).normalize();
  const _pos = new THREE.Vector3();
  const _m = new THREE.Matrix4();
  const _up = new THREE.Vector3(0, 1, 0);
  const _origin = new THREE.Vector3(0, 0, 0);
  const _c = new THREE.Color();

  /** Cloud cover, 0..1. Sticky across `setTime` calls — see {@link SkyHandle}. */
  let overcast = 0;
  /** The last {@link SkyTime}, so `setWeather` can re-apply without a clock. */
  let lastTime = /** @type {SkyTime} */ ({ night: 0, sunset: 0 });

  /** @param {SkyTime} t */
  function setTime(t = {}) {
    lastTime = t;
    const night = clamp01(t.night ?? 0);
    const sunset = clamp01(t.sunset ?? t.warm ?? 0);

    if (t.sunDir) _sunDir.set(t.sunDir.x, t.sunDir.y, t.sunDir.z).normalize();
    uSunDir.value.copy(_sunDir);

    mix3(uGround.value, STOPS.day.ground, STOPS.dusk.ground, STOPS.night.ground, sunset, night);
    mix3(uHorizon.value, STOPS.day.horizon, STOPS.dusk.horizon, STOPS.night.horizon, sunset, night);
    mix3(uMid.value, STOPS.day.mid, STOPS.dusk.mid, STOPS.night.mid, sunset, night);
    mix3(uZenith.value, STOPS.day.zenith, STOPS.dusk.zenith, STOPS.night.zenith, sunset, night);

    // Cloud, over whatever the cycle just produced. See `cloudStop`.
    cloudStop(uGround.value, overcast, _c);
    cloudStop(uHorizon.value, overcast, _c);
    cloudStop(uMid.value, overcast, _c);
    cloudStop(uZenith.value, overcast, _c);

    // Everything the cloud DECK is in front of goes with it. The halo dies
    // fastest (a sun halo through storm cloud is the single most obviously
    // wrong thing the dome can show), the stars next, the discs last — a moon
    // still faintly readable through thin cloud is a real sight.
    const clear = 1 - overcast;

    // Halo: mild at noon, molten at the golden hour, gone after dark.
    uGlowColor.value.set(SUN_DAY).lerp(_c.set(SUN_DUSK), sunset);
    uGlowAmt.value = (1 - night) * (0.16 + sunset * 0.9) * (1 - overcast * 0.94);

    // ^2.2, not ^1: stars must stay invisible through a bright dusk and only
    // arrive once the sky has actually gone dark.
    uStarOpacity.value = Math.pow(night, 2.2) * 0.95 * (1 - overcast * 0.96);

    // Sun: fades out as it dips under the horizon, and goes orange on the way.
    const above = smooth01(-0.1, 0.05, _sunDir.y);
    uSunOpacity.value = above * (1 - night * 0.9) * (1 - overcast * 0.9);
    uSunColor.value
      .set(SUN_DAY)
      .lerp(_c.set(SUN_DUSK), sunset)
      .multiplyScalar(2.6 - sunset * 0.5);
    placeDisc(sun, _pos.copy(_sunDir).multiplyScalar(radius * 0.92));

    // Moon rides the antipodal arc.
    const moonUp = smooth01(-0.06, 0.1, -_sunDir.y);
    uMoonOpacity.value = moonUp * night * (0.12 + clear * 0.88);
    placeDisc(moon, _pos.copy(_sunDir).multiplyScalar(-radius * 0.92));
  }

  /** @param {{overcast?: number}} [w] */
  function setWeather(w = {}) {
    if (w.overcast != null) overcast = clamp01(w.overcast);
    setTime(lastTime);
  }

  /**
   * @param {THREE.Mesh} mesh
   * @param {THREE.Vector3} localPos
   */
  function placeDisc(mesh, localPos) {
    mesh.position.copy(localPos);
    // Matrix4.lookAt(eye, target, up) makes +Z point eye->target... backwards for
    // us, so pass (origin, localPos): +Z then points from the disc at the dome
    // centre, i.e. straight at the camera.
    _m.lookAt(_origin, localPos, _up);
    mesh.quaternion.setFromRotationMatrix(_m);
  }

  setTime({ night: 0, sunset: 0 });

  let disposed = false;

  return {
    object: group,
    setTime,
    setWeather,
    palette: STOPS,
    dispose() {
      if (disposed) return;
      disposed = true;
      group.removeFromParent();
      domeGeo.dispose();
      domeMat.dispose();
      discGeo.dispose();
      sunMat.dispose();
      moonMat.dispose();
      starGeo?.dispose();
      starMat?.dispose();
    },
  };
}

/**
 * Adds a sky to `engine.scene` AND pins it to the camera every frame, which is
 * what keeps a 450m dome inside a 600m far plane no matter how big the level is.
 * Also clears `engine.scene.background` (the dome is the background now).
 *
 * Registered as an `engine.systems` entry, so `engine.dispose()` frees it even
 * if nothing else does.
 *
 * @param {Engine} engine
 * @param {Parameters<typeof createSky>[0]} [opts]
 * @returns {SkyHandle}
 */
export function attachSky(engine, opts = {}) {
  const sky = createSky(opts);
  const prevBackground = engine.scene.background;

  engine.scene.add(sky.object);
  engine.scene.background = null;

  let detached = false;
  const detach = () => {
    if (detached) return;
    detached = true;
    engine.scene.background = prevBackground;
    const i = engine.systems.indexOf(system);
    if (i >= 0) engine.systems.splice(i, 1);
    sky.dispose();
  };

  const system = {
    name: 'sky',
    /** Runs before `Engine.render()`, so the matrix update inside the renderer
     *  picks the new position up in the same frame. */
    render() {
      sky.object.position.copy(engine.camera.position);
    },
    dispose: detach,
  };
  engine.systems.push(system);

  return {
    object: sky.object,
    setTime: sky.setTime,
    setWeather: sky.setWeather,
    palette: sky.palette,
    dispose: detach,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// internals
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Stars as one merged non-indexed quad soup rather than `THREE.Points`: point
 * size above 1px is not portable across the WebGPU/WebGL2 backends, and quads
 * give us a soft round falloff and per-star twinkle for free. One draw call.
 *
 * @param {number} count
 * @param {number} r Distance from the dome centre.
 * @param {string|number} seed
 * @returns {THREE.BufferGeometry}
 */
function buildStarQuads(count, r, seed) {
  const rng = streamRng(seed, 'stars');
  const pos = new Float32Array(count * 6 * 3);
  const uvs = new Float32Array(count * 6 * 2);
  const tint = new Float32Array(count * 6 * 3);
  const phase = new Float32Array(count * 6);

  const dir = new THREE.Vector3();
  const right = new THREE.Vector3();
  const up = new THREE.Vector3();
  const tmp = new THREE.Vector3();
  const col = new THREE.Color();
  const WORLD_UP = new THREE.Vector3(0, 1, 0);

  // Quad corners as (u, v) with the local offsets they map to.
  const CORNERS = [
    [-0.5, -0.5, 0, 0],
    [0.5, -0.5, 1, 0],
    [0.5, 0.5, 1, 1],
    [-0.5, -0.5, 0, 0],
    [0.5, 0.5, 1, 1],
    [-0.5, 0.5, 0, 1],
  ];

  for (let i = 0; i < count; i++) {
    // Uniform in y IS uniform on the sphere (Archimedes). The band is cut at
    // -0.12 rather than 0 so the horizon does not look sliced.
    const y = range(rng, -0.12, 1);
    const a = rng() * Math.PI * 2;
    const rad = Math.sqrt(Math.max(0, 1 - y * y));
    dir.set(Math.cos(a) * rad, y, Math.sin(a) * rad).normalize();

    right.crossVectors(dir, WORLD_UP);
    if (right.lengthSq() < 1e-6) right.set(1, 0, 0);
    right.normalize();
    up.crossVectors(right, dir).normalize();

    // Size + brightness are correlated so the sky has a few real "stars" and a
    // lot of dust, instead of uniform confetti.
    const mag = Math.pow(rng(), 2.4);
    const size = (r / 450) * (1.1 + mag * 5.2);
    const bright = 0.3 + mag * 1.5;

    // Mostly blue-white; a minority of amber giants.
    const warm = rng() < 0.18;
    col.setRGB(
      warm ? 1 : 0.78 + rng() * 0.22,
      warm ? 0.82 : 0.86 + rng() * 0.14,
      warm ? 0.62 : 1,
    ).multiplyScalar(bright);

    const ph = rng() * Math.PI * 2;

    for (let c = 0; c < 6; c++) {
      const [ox, oy, u0, v0] = CORNERS[c];
      tmp.copy(dir).multiplyScalar(r);
      tmp.addScaledVector(right, ox * size);
      tmp.addScaledVector(up, oy * size);

      const vi = i * 6 + c;
      pos[vi * 3 + 0] = tmp.x;
      pos[vi * 3 + 1] = tmp.y;
      pos[vi * 3 + 2] = tmp.z;
      uvs[vi * 2 + 0] = u0;
      uvs[vi * 2 + 1] = v0;
      tint[vi * 3 + 0] = col.r;
      tint[vi * 3 + 1] = col.g;
      tint[vi * 3 + 2] = col.b;
      phase[vi] = ph;
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geo.setAttribute('starTint', new THREE.BufferAttribute(tint, 3));
  geo.setAttribute('starPhase', new THREE.BufferAttribute(phase, 1));
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), r * 1.05);
  return geo;
}

/**
 * A soft disc on a unit quad: hard-ish core plus a wide halo, additive.
 * @param {string} name
 * @param {any} colorUniform vec3 uniform (values >1 so the disc feeds bloom).
 * @param {any} opacityUniform float uniform.
 * @param {number} coreRadius 0..1 of the quad's half-extent.
 * @param {number} haloPow Higher = tighter halo.
 * @returns {THREE.MeshBasicNodeMaterial}
 */
function discMaterial(name, colorUniform, opacityUniform, coreRadius, haloPow) {
  const m = new THREE.MeshBasicNodeMaterial();
  m.name = name;
  m.transparent = true;
  m.depthWrite = false;
  m.blending = THREE.AdditiveBlending;
  m.fog = false;
  m.toneMapped = false;

  const d = length(uv().sub(vec2(0.5, 0.5))).mul(2);
  const core = smoothstep(float(coreRadius), float(coreRadius * 0.82), d);
  const halo = pow(smoothstep(float(1.0), float(0.0), d), float(haloPow)).mul(0.5);

  m.colorNode = colorUniform;
  m.opacityNode = clamp(core.add(halo), float(0), float(1)).mul(opacityUniform);
  return m;
}

/** @param {number} v */
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * Hermite smoothstep — duplicated from math/mathx so sky.js has no import cycle
 * risk with the systems layer.
 * @param {number} e0
 * @param {number} e1
 * @param {number} x
 */
function smooth01(e0, e1, x) {
  const t = clamp01(e1 === e0 ? 0 : (x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
}
